/**
 * Service Cloudinary pour l'upload de fichiers
 * 
 * @description Gère tous les uploads de fichiers (documents, images, CV) vers Cloudinary
 * et retourne les URLs sécurisées pour stockage dans le backend
 * @since 2025-12-26
 */

// Configuration Cloudinary
const CLOUDINARY_CONFIG = {
  cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'daxvxdecv',
  uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'unsigned_preset',
  apiKey: import.meta.env.VITE_CLOUDINARY_API_KEY || '',
};

// Types de fichiers et leurs dossiers Cloudinary
export type UploadFolder = 
  | 'company/logos'
  | 'company/documents'
  | 'company/cv'
  | 'institution/logos'
  | 'institution/documents'
  | 'user/avatars'
  | 'user/identity'
  | 'user/documents'
  | 'payments/documents'
  | 'general';

export interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  url: string;
  format: string;
  resource_type: string;
  bytes: number;
  width?: number;
  height?: number;
  original_filename: string;
  created_at: string;
}

export interface UploadedFileInfo {
  id: string;
  name: string;
  url: string;
  publicId: string;
  size: number;
  type: string;
  uploadedAt: Date;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class CloudinaryService {
  private cloudName: string;
  private uploadPreset: string;

  constructor() {
    this.cloudName = CLOUDINARY_CONFIG.cloudName;
    this.uploadPreset = CLOUDINARY_CONFIG.uploadPreset;
    
    if (!this.cloudName) {
      console.warn('⚠️ VITE_CLOUDINARY_CLOUD_NAME non configuré, utilisation de la valeur par défaut');
    }
  }

  /**
   * Génère l'URL d'upload Cloudinary
   */
  private getUploadUrl(): string {
    return `https://api.cloudinary.com/v1_1/${this.cloudName}/auto/upload`;
  }

  /**
   * Génère un ID unique pour le fichier
   */
  private generateFileId(): string {
    return `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Valide un fichier avant upload
   */
  validateFile(file: File, options: {
    maxSize?: number; // en MB
    allowedTypes?: string[];
  } = {}): { valid: boolean; error?: string } {
    const { maxSize = 10, allowedTypes } = options;

    // Vérifier la taille
    if (file.size > maxSize * 1024 * 1024) {
      return {
        valid: false,
        error: `Le fichier "${file.name}" dépasse la taille maximale de ${maxSize}MB`
      };
    }

    // Vérifier le type si spécifié
    if (allowedTypes && allowedTypes.length > 0) {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const mimeTypeValid = allowedTypes.some(type => {
        if (type.startsWith('.')) {
          return type.toLowerCase() === fileExtension;
        }
        return file.type.includes(type);
      });

      if (!mimeTypeValid) {
        return {
          valid: false,
          error: `Le type de fichier "${fileExtension}" n'est pas accepté. Types autorisés: ${allowedTypes.join(', ')}`
        };
      }
    }

    return { valid: true };
  }

  /**
   * Upload un fichier vers Cloudinary
   */
  async uploadFile(
    file: File,
    folder: UploadFolder = 'general',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);
    formData.append('folder', `wanzo_land/${folder}`);
    
    // Ajouter des tags pour l'organisation
    formData.append('tags', `wanzo,${folder.replace('/', '_')}`);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Suivi de la progression
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress({
              loaded: event.loaded,
              total: event.total,
              percentage: Math.round((event.loaded / event.total) * 100)
            });
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result: CloudinaryUploadResult = JSON.parse(xhr.responseText);
            
            const uploadedFile: UploadedFileInfo = {
              id: this.generateFileId(),
              name: file.name,
              url: result.secure_url,
              publicId: result.public_id,
              size: result.bytes,
              type: file.type || result.format,
              uploadedAt: new Date(result.created_at)
            };

            console.log('✅ Fichier uploadé sur Cloudinary:', uploadedFile.url);
            resolve(uploadedFile);
          } catch (parseError) {
            reject(new Error('Erreur lors du parsing de la réponse Cloudinary'));
          }
        } else {
          let errorMessage = 'Erreur lors de l\'upload';
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            console.error('❌ Réponse erreur Cloudinary:', errorResponse);
            errorMessage = errorResponse.error?.message || errorMessage;
          } catch {
            console.error('❌ Réponse brute Cloudinary:', xhr.responseText);
          }
          console.error('❌ Status HTTP:', xhr.status, xhr.statusText);
          reject(new Error(`${errorMessage} (HTTP ${xhr.status})`));
        }
      };

      xhr.onerror = (event) => {
        console.error('❌ Erreur réseau XHR:', event);
        console.error('❌ URL tentée:', this.getUploadUrl());
        console.error('❌ Preset utilisé:', this.uploadPreset);
        reject(new Error(`Erreur réseau lors de l'upload. Vérifiez votre connexion internet et que le preset "${this.uploadPreset}" est bien configuré en mode "Unsigned" sur Cloudinary.`));
      };

      xhr.ontimeout = () => {
        reject(new Error('Timeout lors de l\'upload (60s dépassées)'));
      };

      // Log pour debugging
      console.log('📤 Upload vers Cloudinary:', {
        url: this.getUploadUrl(),
        preset: this.uploadPreset,
        folder: folder,
        fileName: file.name,
        fileSize: file.size
      });

      xhr.open('POST', this.getUploadUrl());
      xhr.timeout = 60000; // 60 secondes timeout
      xhr.send(formData);
    });
  }

  /**
   * Upload plusieurs fichiers en parallèle
   */
  async uploadMultipleFiles(
    files: File[],
    folder: UploadFolder = 'general',
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<UploadedFileInfo[]> {
    const uploadPromises = files.map((file, index) => 
      this.uploadFile(
        file, 
        folder,
        onProgress ? (progress) => onProgress(index, progress) : undefined
      )
    );

    const results = await Promise.allSettled(uploadPromises);
    
    const successfulUploads: UploadedFileInfo[] = [];
    const errors: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulUploads.push(result.value);
      } else {
        errors.push(`${files[index].name}: ${result.reason.message}`);
      }
    });

    if (errors.length > 0) {
      console.warn('⚠️ Certains fichiers n\'ont pas pu être uploadés:', errors);
    }

    return successfulUploads;
  }

  /**
   * Upload d'un logo (optimisé pour les images)
   */
  async uploadLogo(
    file: File, 
    entityType: 'company' | 'institution',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    // Valider le fichier image
    const validation = this.validateFile(file, {
      maxSize: 5,
      allowedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
    });

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const folder: UploadFolder = entityType === 'company' ? 'company/logos' : 'institution/logos';
    return this.uploadFile(file, folder, onProgress);
  }

  /**
   * Upload d'un document (PDF, Word, etc.)
   */
  async uploadDocument(
    file: File,
    entityType: 'company' | 'institution' | 'user' | 'payments',
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    // Valider le fichier document
    const validation = this.validateFile(file, {
      maxSize: 20,
      allowedTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png']
    });

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const folder: UploadFolder = `${entityType}/documents` as UploadFolder;
    return this.uploadFile(file, folder, onProgress);
  }

  /**
   * Upload d'un CV
   */
  async uploadCV(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    // Valider le fichier CV
    const validation = this.validateFile(file, {
      maxSize: 10,
      allowedTypes: ['.pdf', '.doc', '.docx']
    });

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return this.uploadFile(file, 'company/cv', onProgress);
  }

  /**
   * Upload d'un avatar utilisateur
   */
  async uploadAvatar(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    // Valider le fichier image
    const validation = this.validateFile(file, {
      maxSize: 5,
      allowedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    });

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return this.uploadFile(file, 'user/avatars', onProgress);
  }

  /**
   * Upload d'un document d'identité
   */
  async uploadIdentityDocument(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadedFileInfo> {
    // Valider le fichier
    const validation = this.validateFile(file, {
      maxSize: 10,
      allowedTypes: ['.pdf', '.jpg', '.jpeg', '.png']
    });

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    return this.uploadFile(file, 'user/identity', onProgress);
  }

  /**
   * Génère une URL de transformation Cloudinary
   */
  getTransformedUrl(
    url: string, 
    transformations: {
      width?: number;
      height?: number;
      crop?: 'fill' | 'fit' | 'scale' | 'thumb';
      quality?: 'auto' | number;
      format?: 'auto' | 'webp' | 'jpg' | 'png';
    } = {}
  ): string {
    if (!url.includes('cloudinary.com')) {
      return url;
    }

    const { width, height, crop = 'fill', quality = 'auto', format = 'auto' } = transformations;
    
    const parts: string[] = [];
    if (width) parts.push(`w_${width}`);
    if (height) parts.push(`h_${height}`);
    if (crop) parts.push(`c_${crop}`);
    if (quality) parts.push(`q_${quality}`);
    if (format) parts.push(`f_${format}`);
    
    if (parts.length === 0) return url;
    
    const transformation = parts.join(',');
    
    // Insérer la transformation dans l'URL
    return url.replace('/upload/', `/upload/${transformation}/`);
  }

  /**
   * Génère une URL de téléchargement forcé
   */
  getDownloadUrl(url: string, filename?: string): string {
    if (!url.includes('cloudinary.com')) {
      return url;
    }

    const attachment = filename 
      ? `fl_attachment:${encodeURIComponent(filename)}`
      : 'fl_attachment';
    
    return url.replace('/upload/', `/upload/${attachment}/`);
  }
}

// Instance singleton exportée
export const cloudinaryService = new CloudinaryService();
export default cloudinaryService;
